import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { getCachedValue, LocalStorageKeys } from '@shared-utils';
import { Bmi, ServiceCodesEnum } from '@types-lib';
import { distinctUntilChanged, filter, pipe, tap, throttleTime } from 'rxjs';
import { BmiStoreProps, BmiStoreResourcesProps } from './bmi-store.model';
import { initialBmiState } from './bmi-store.state';

const biometricsListLength =
  getCachedValue(LocalStorageKeys.PIMS_PARAMETERS)?.pimsVisitParameters?.biometrics?.listLength || 300;

export function withBmiStoreMethods<_>() {
  return signalStoreFeature(
    {
      props: type<BmiStoreProps & BmiStoreResourcesProps>(),
      state: type<Bmi.BmiStoreState>(),
    },
    withMethods(store => ({
      setPatientUserId: (patientUserId: number) =>
        patchState(store, {
          patientUserId,
        }),
      setSortType: (sortType: 'asc' | 'desc') =>
        patchState(store, {
          bmiListOptions: {
            ...store.bmiListOptions(),
            sort: sortType,
          },
        }),
      setServiceCodesFilter: (serviceCodes: ServiceCodesEnum[]) =>
        patchState(store, {
          bmiListOptions: {
            ...store.bmiListOptions(),
            filterBy: { serviceCodes },
          },
        }),
      setBmiItemEditMode: (mail_order_fill_request_id: number, editState: boolean) => {
        patchState(store, { itemBeingEditedId: editState ? mail_order_fill_request_id : null });
      },
      loadBmiData: rxMethod<Bmi.LoadBmiDataReq | undefined>(
        pipe(
          filter(loadBmiDataReq => !!loadBmiDataReq),
          throttleTime(2000),
          distinctUntilChanged(),
          tap(req => {
            const { userId, limit, order, serviceCodesFilter, offset, visitId } = req;
            patchState(store, {
              patientUserId: userId,
              bmiListOptions: {
                ...store.bmiListOptions(),
                pagination: {
                  ...store.bmiListOptions().pagination,
                  limit: limit || biometricsListLength,
                  order: order || 'desc',
                  offset: offset || 0,
                  service_codes: serviceCodesFilter || [],
                },
                sort: order || 'desc',
                filterBy: {
                  serviceCodes: serviceCodesFilter || [],
                },
              },
            });
          }),
        ),
      ),
      updateTreatmentStartInfo: rxMethod<Bmi.TreatmentStartInfoSaveReq>(
        pipe(
          throttleTime(2000),
          distinctUntilChanged(),
          tap(updateTreatmentStartReq => {
            store._updateTreatmentStartInfoTrigger.update(() => ({
              ...updateTreatmentStartReq,
            }));
          }),
        ),
      ),
      updateBmiItem: rxMethod<Bmi.SaveBmiItemReq>(
        pipe(
          throttleTime(2000),
          distinctUntilChanged(),
          tap(updateBmiItemReq => {
            store._updateBmiItemTrigger.update(() => ({
              ...updateBmiItemReq,
            }));
          }),
        ),
      ),
      resetStoreToInitialState: () =>
        patchState(store, {
          ...initialBmiState,
        }),
    })),
  );
}
